import { useMemo } from "react";

import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { filledToggleGroupSx } from "@shared";
import { format } from "date-fns";

import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useDutyScheduleCounselorOptions } from "@/pages/duty-schedule/hooks/useDutyScheduleCounselorOptions";
import { useDutyScheduleDetailContext } from "@/pages/duty-schedule/pages/duty-schedule-detail/DutyScheduleDetail.types";
import { DutyScheduleAssignmentTable } from "@/pages/duty-schedule/pages/duty-schedule-detail/components/assignment-table/DutyScheduleAssignmentTable";
import { useTranslation } from "@/translations";

import {
  dateKey,
  datesWithCounselor,
  groupWorkingDaysByMonth,
  userCodesByCounselorId,
} from "./DutyScheduleDataEntry.utils";

const MONTH_PATTERN = "MMMM yyyy";

// The filter state, the pending edits and the save action live on the detail page rather than here,
// because this component unmounts whenever another tab is opened.
export function DutyScheduleDataEntry() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { dutySchedule, isEditable, selectedMonthKey, onSelectMonth, counselorFilter, resolveCell, onAssign } =
    useDutyScheduleDetailContext();
  const { counselors } = useDutyScheduleCounselorOptions();

  const months = useMemo(() => groupWorkingDaysByMonth(dutySchedule.workingDayDates), [dutySchedule.workingDayDates]);

  const [firstMonth] = months;
  const selectedMonth = months.find((month) => month.key === selectedMonthKey) ?? firstMonth;

  const userCodes = useMemo(() => userCodesByCounselorId(dutySchedule.slots), [dutySchedule.slots]);

  const filteredDates = useMemo(
    () => (counselorFilter == null ? null : datesWithCounselor(dutySchedule.slots, counselorFilter)),
    [counselorFilter, dutySchedule.slots]
  );

  const visibleWorkingDays = useMemo(() => {
    const workingDays = selectedMonth?.workingDays ?? [];

    return filteredDates == null ? workingDays : workingDays.filter((date) => filteredDates.has(dateKey(date)));
  }, [selectedMonth, filteredDates]);

  if (selectedMonth == null) {
    return <Typography>{t("dutySchedule.detail.dataEntry.noWorkingDays")}</Typography>;
  }

  const filterHint =
    counselorFilter == null
      ? null
      : t("dutySchedule.detail.dataEntry.filter.hint", {
          matching: visibleWorkingDays.length,
          total: selectedMonth.workingDays.length,
          userCodes: counselorFilter.map((counselorId) => userCodes.get(counselorId) ?? "").join(", "),
        });

  return (
    <Stack flex={1} minHeight={0} gap={2}>
      <ToggleButtonGroup
        color="primary"
        exclusive
        value={selectedMonth.key}
        aria-label={t("dutySchedule.detail.dataEntry.monthTabsAria")}
        onChange={(_event, next: string | null) => {
          if (next !== null) {
            onSelectMonth(next);
          }
        }}
        sx={filledToggleGroupSx}
      >
        {months.map((month) => (
          <ToggleButton key={month.key} value={month.key}>
            <Stack direction="row" alignItems="baseline" gap={0.75}>
              <Typography variant="body2" fontWeight={700}>
                {format(new Date(month.year, month.month - 1, 1), MONTH_PATTERN, { locale: locales[language] })}
              </Typography>
              {/* Deliberately the unfiltered count: the tabs say how big each month is, not how much
                  of it the current filter leaves. */}
              <Typography variant="caption" color="text.secondary">
                {month.workingDays.length}
              </Typography>
            </Stack>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {filterHint != null && (
        <Typography variant="body2" color="text.secondary">
          {filterHint}
        </Typography>
      )}

      <DutyScheduleAssignmentTable
        workingDays={visibleWorkingDays}
        columns={dutySchedule.columns}
        isEditable={isEditable}
        counselors={counselors}
        counselorFilter={counselorFilter}
        resolveCell={resolveCell}
        onAssign={onAssign}
      />
    </Stack>
  );
}
